// Everything the Usage screens show that is not a fixture verbatim: the
// session's state applied to the projects, the Pipeline's act-by and next
// action, the Portfolio's tallies and tree. One place, so a number that
// appears on two screens is computed once.

import {
    DEALS,
    DEMO_TODAY,
    DRILLABLE_DEAL_IDS,
    E3_UNIT_PRICE,
    E5_UNIT_PRICE,
    MICROSOFT_EA,
    OWNER_REVIEW_GROUP,
    PROJECTS,
    VENDORS,
} from "./fixtures";
import { daysUntil, demoDatePlus } from "./format";
import type { UsageDemoState } from "./store";
import type {
    ActionItem,
    ProjectStage,
    UsageCategory,
    UsageDeal,
    UsageProject,
    UsageVendor,
} from "./types";

export const CATEGORIES: UsageCategory[] = [
    "Software",
    "Cloud and data",
    "Observability and security",
    "AI",
    "Market data",
];

const vendorsById = new Map(VENDORS.map((v) => [v.id, v]));
const dealsById = new Map(DEALS.map((d) => [d.id, d]));

export const vendorById = (id: string): UsageVendor | undefined => vendorsById.get(id);
export const dealById = (id: string): UsageDeal | undefined => dealsById.get(id);
export const dealsOfVendor = (vendorId: string): UsageDeal[] =>
    DEALS.filter((d) => d.vendorId === vendorId);

/** Total contract value: the annual cost over the term's years; one year when the start is unknown. */
export const contractValue = (deal: UsageDeal): number => {
    if (!deal.term.start) return deal.annualCost;
    const [y0, m0] = deal.term.start.split("-").map(Number);
    const [y1, m1] = deal.term.renews.split("-").map(Number);
    const years = Math.max(1, Math.round(((y1 - y0) * 12 + (m1 - m0) + 1) / 12));
    return deal.annualCost * years;
};

/**
 * What the deal uses of what it pays for, as a share: active users over
 * licenses for a seat deal, consumption over the commitment for a
 * consumption deal, which runs past 100% when an overage is ahead.
 */
export const utilizationOf = (deal: UsageDeal): number | undefined =>
    deal.type === "seat" ? deal.utilizationPct : deal.pacePct;

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);

/** How a utilization scores: under 70% red, under 85% orange, green up to 100%, red again past the commitment. */
export const utilizationTier = (percent: number): "red" | "orange" | "green" =>
    percent < 70 || percent > 100 ? "red" : percent < 85 ? "orange" : "green";

/** The deals' utilization, weighted by annual cost, over those with a reading. */
export const portfolioUtilization = (deals: UsageDeal[]): number | undefined => {
    const read = deals.filter((d) => utilizationOf(d) != null);
    const weight = sum(read.map((d) => d.annualCost));
    return weight > 0 ? sum(read.map((d) => d.annualCost * (utilizationOf(d) ?? 0))) / weight : undefined;
};

// ---- Consumption against a commitment ------------------------------------
//
// A commitment is an annual amount; its year ends at the renewal date. The
// four numbers the consumption page leads with come from here, and the
// Portfolio, its banner and the Pipeline tally the same ones.

const DAY = 24 * 60 * 60 * 1000;
const COMMITMENT_DAYS = 365;

/** The commitment year: the 365 days ending on the renewal date, and how far into it today is. */
export const commitmentYear = (deal: UsageDeal) => {
    const end = new Date(deal.term.renews + "T00:00:00Z").getTime();
    // The renewal date is the year's last day, so the first is 364 days before it.
    const start = end - (COMMITMENT_DAYS - 1) * DAY;
    const today = new Date(DEMO_TODAY + "T00:00:00Z").getTime();
    const elapsed = Math.min(COMMITMENT_DAYS, Math.max(0, Math.round((today - start) / DAY)));
    return { days: COMMITMENT_DAYS, elapsed, remaining: COMMITMENT_DAYS - elapsed, share: elapsed / COMMITMENT_DAYS };
};

/** A consumption deal's run-rate over the last 90 days, USD a year: the commitment times the pace. */
export const consumptionOf = (deal: UsageDeal): number | undefined =>
    deal.type === "consumption" && deal.pacePct != null ? (deal.annualCost * deal.pacePct) / 100 : undefined;

/** Consumed since the commitment year began: the fixture's figure, or the pace held all year. */
export const consumedToDate = (deal: UsageDeal): number | undefined => {
    const runRate = consumptionOf(deal);
    if (runRate == null) return undefined;
    return deal.commitment?.consumedToDate ?? runRate * commitmentYear(deal).share;
};

/** Where the year ends at the last 90 days' pace: consumed so far plus the run-rate over the days left. */
export const projectedConsumption = (deal: UsageDeal): number | undefined => {
    const consumed = consumedToDate(deal);
    const runRate = consumptionOf(deal);
    if (consumed == null || runRate == null) return undefined;
    return consumed + (runRate * commitmentYear(deal).remaining) / COMMITMENT_DAYS;
};

/** The month the projection crosses the commitment, as "YYYY-MM"; undefined when it does not. */
export const commitmentReachedMonth = (deal: UsageDeal): string | undefined => {
    const consumed = consumedToDate(deal);
    const runRate = consumptionOf(deal);
    if (consumed == null || runRate == null || consumed >= deal.annualCost) return undefined;
    const days = ((deal.annualCost - consumed) / runRate) * COMMITMENT_DAYS;
    if (days > commitmentYear(deal).remaining) return undefined;
    const at = new Date(new Date(DEMO_TODAY + "T00:00:00Z").getTime() + days * DAY);
    return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, "0")}`;
};

/**
 * The projected overage, USD: what the year's consumption will exceed the
 * commitment by, billed at the on-demand rate. Zero when it stays within.
 */
export const overageOf = (deal: UsageDeal): number => {
    const projected = projectedConsumption(deal);
    if (projected == null) return 0;
    const premium = 1 + (deal.commitment?.onDemandPremiumPct ?? 0) / 100;
    return Math.max(0, projected - deal.annualCost) * premium;
};

/**
 * What the deal pays for and does not use, USD a year: idle seats at the
 * unit price (annual cost over seats bought), or the share of the commitment
 * the year's projected consumption leaves unused. Zero without a reading.
 */
export const unusedOf = (deal: UsageDeal): number => {
    if (deal.seats) return ((deal.seats.bought - deal.seats.active) * deal.annualCost) / deal.seats.bought;
    const projected = projectedConsumption(deal);
    return projected != null ? Math.max(0, deal.annualCost - projected) : 0;
};

/** The value at stake on the deal, USD a year: unused plus the overage ahead, before any negotiation. */
export const atStakeOf = (deal: UsageDeal): number => unusedOf(deal) + overageOf(deal);

/** The vendor of a deal; every fixture deal has one. */
export const vendorOfDeal = (deal: UsageDeal): UsageVendor => vendorsById.get(deal.vendorId)!;

// ---- Projects with the session applied -----------------------------------

const SLA_HOURS = 24;

/** A project created by "Request usage analysis": ours to deliver within the SLA. */
const requestedProject = (dealId: string): UsageProject => ({
    id: `usage-requested-${dealId}`,
    name: "Usage analysis",
    dealId,
    origin: "requested",
    trigger: "Usage not connected; analysis requested",
    stage: "usage_analysis",
    estSavings: 0,
    estSavingsLabel: "To be analyzed",
    analyses: [],
    actionItems: [
        {
            id: `usage-requested-${dealId}:analysis`,
            projectId: `usage-requested-${dealId}`,
            stage: "usage",
            title: "Usage analysis",
            origin: "requested",
            why: `You requested it today; due within ${SLA_HOURS}h`,
            ownerKind: "infinityloop",
            ownerLabel: "InfinityLoop",
            due: demoDatePlus(1),
            status: "open",
            note: "Requested today",
        },
    ],
    isNew: true,
});

/** Where a snoozed project's items are pushed to. */
export const SNOOZE_UNTIL = "2026-11-15";

/** The fixture projects plus the session's, with its decisions applied. */
export const effectiveProjects = (state: UsageDemoState): UsageProject[] => {
    const fromFixtures = PROJECTS.map((project) => {
        const actionItems = project.actionItems.map((item) =>
            state.itemStatus[item.id] ? { ...item, status: state.itemStatus[item.id] } : item
        );
        const analyses = [...project.analyses];
        if (state.refreshed[project.id]) {
            const latest = analyses.filter((a) => a.kind === "usage").length;
            analyses.push({
                kind: "usage",
                revision: latest + 1,
                publishedAt: DEMO_TODAY,
                usageTo: demoDatePlus(-1),
            });
        }
        let stage: ProjectStage = project.stage;
        if (state.playbookRequested[project.id] && !analyses.some((a) => a.kind === "playbook")) {
            analyses.push({ kind: "playbook", revision: 1, publishedAt: DEMO_TODAY, round: 1, inProgress: true });
            stage = "playbook";
        }
        const decision = state.decisions[project.id];
        // Snoozing pushes the open items to the snooze date, so they leave
        // "On me" and come back as upcoming.
        const snoozed = decision === "snoozed"
            ? actionItems.map((item) =>
                  item.status === "open" && item.due < SNOOZE_UNTIL ? { ...item, due: SNOOZE_UNTIL, note: "Snoozed" } : item
              )
            : actionItems;
        return {
            ...project,
            actionItems: snoozed,
            analyses,
            stage: decision === "auto_renew" ? "closed" : stage,
            isNew: project.isNew,
        } as UsageProject;
    });
    return [...fromFixtures, ...state.requestedDeals.map(requestedProject)];
};

export const projectForDeal = (dealId: string, projects: UsageProject[]): UsageProject | undefined =>
    projects.find((p) => p.dealId === dealId && p.stage !== "closed");

// ---- Pipeline ------------------------------------------------------------

export interface NextAction {
    actBy: string;
    label: string;
    items: ActionItem[];
}

/**
 * The earliest open items of the user's set the row's act-by. Several on
 * the same day that belong together read as one chase ("Chase 2 owner
 * reviews"); otherwise the first one names the action.
 */
export const nextAction = (project: UsageProject): NextAction | undefined => {
    const open = project.actionItems.filter(isNextMove).sort((a, b) => a.due.localeCompare(b.due));
    if (open.length === 0) return undefined;
    const actBy = open[0].due;
    const sameDay = open.filter((item) => item.due === actBy);
    const grouped = sameDay.filter((item) => item.group && item.group === sameDay[0].group);
    if (grouped.length > 1 && grouped[0].group === OWNER_REVIEW_GROUP) {
        return { actBy, label: `Chase ${grouped.length} owner reviews`, items: grouped };
    }
    return { actBy, label: open[0].title, items: [open[0]] };
};

/** Whether a stage's work is over: every item done or canceled. The next stage waits for it. */
export const stageComplete = (project: UsageProject, stage: ActionItem["stage"]): boolean =>
    project.actionItems.filter((item) => item.stage === stage).every((item) => item.status !== "open");

/** An open item assigned to the signed-in user, whenever it is due: what the Pipeline lists and the badges count. */
export const isNextMove = (item: ActionItem): boolean => item.status === "open" && item.ownerKind === "user";

const hasNextMove = (project: UsageProject) => project.actionItems.some(isNextMove);

/** How many next moves there are across the projects: the Pipeline's badge. */
export const nextMoveCount = (projects: UsageProject[]): number =>
    projects.reduce((n, p) => n + p.actionItems.filter(isNextMove).length, 0);

/**
 * The Pipeline's two sections: projects with an item on the user, and the
 * active projects with nothing on them, waiting on InfinityLoop or on
 * someone else.
 */
export const pipelineSections = (projects: UsageProject[]) => {
    const listed = pipelineProjects(projects);
    return {
        nextMoves: listed.filter(hasNextMove),
        waiting: listed.filter((p) => !hasNextMove(p)),
    };
};

/** The projects closed this year, with what they realized: the Pipeline's realized savings. */
export const closedProjects = (projects: UsageProject[]): UsageProject[] => projects.filter((p) => p.stage === "closed");

export const realizedSavings = (projects: UsageProject[]): number =>
    closedProjects(projects).reduce((sum, p) => sum + (p.realizedSavings ?? 0), 0);

/** The projects the Pipeline lists, soonest act-by first; closed ones leave it. */
export const pipelineProjects = (projects: UsageProject[]): UsageProject[] =>
    projects
        .filter((p) => p.stage !== "closed")
        .sort((a, b) => (nextAction(a)?.actBy ?? "9999").localeCompare(nextAction(b)?.actBy ?? "9999"));

export const stageLabel = (project: UsageProject): string => {
    switch (project.stage) {
        case "proposed":
            return "Proposed";
        case "usage_analysis":
            return "Usage analysis";
        // A playbook in hand or on its way: the negotiation is on.
        case "playbook":
        case "negotiating":
            return "Negotiating";
        case "closed":
            return "Closed";
    }
};

/** Stages the fixed-length bar fills, in order. */
export const STAGES: ProjectStage[] = ["proposed", "usage_analysis", "playbook", "negotiating", "closed"];

// ---- Portfolio -----------------------------------------------------------

export type DealStatus = "project_open" | "analysis_available" | "not_analyzed" | "not_connected";

export const dealStatus = (deal: UsageDeal, projects: UsageProject[]): DealStatus => {
    if (projectForDeal(deal.id, projects)) return "project_open";
    if (deal.usageStatus === "not_connected") return "not_connected";
    if (deal.valueAtStake != null) return "analysis_available";
    return "not_analyzed";
};

export const DEAL_STATUS_LABEL: Record<DealStatus, string> = {
    project_open: "Project open",
    analysis_available: "Analysis available",
    not_analyzed: "Not analyzed",
    not_connected: "Usage not connected",
};

export const RENEWAL_WINDOW_DAYS = 180;

export interface PortfolioTotals {
    spend: number;
    connectedSpend: number;
    /** What the connected deals pay for and do not use, and what they will pay beyond their commitments. */
    unused: number;
    overage: { amount: number; contracts: number };
    atStake: number;
    renewals: { count: number; amount: number };
    vendors: number;
    deals: number;
}


export const isConnected = (deal: UsageDeal) => deal.usageStatus === "connected";
export const isAheadOfCommitment = (deal: UsageDeal) =>
    deal.type === "consumption" && (deal.pacePct ?? 0) > 100;
export const renewsWithin = (deal: UsageDeal, days: number) => {
    const left = daysUntil(deal.term.renews);
    return left >= 0 && left <= days;
};

export const portfolioTotals = (deals: UsageDeal[] = DEALS): PortfolioTotals => {
    const ahead = deals.filter(isAheadOfCommitment);
    const renewing = deals.filter((d) => renewsWithin(d, RENEWAL_WINDOW_DAYS));
    return {
        spend: sum(deals.map((d) => d.annualCost)),
        connectedSpend: sum(deals.filter(isConnected).map((d) => d.annualCost)),
        unused: sum(deals.map(unusedOf)),
        overage: { amount: sum(deals.map(overageOf)), contracts: ahead.length },
        atStake: sum(deals.map(atStakeOf)),
        renewals: { count: renewing.length, amount: sum(renewing.map((d) => d.annualCost)) },
        vendors: new Set(deals.map((d) => d.vendorId)).size,
        deals: deals.length,
    };
};

// The Portfolio tree: a group (category, vendor or owner), vendors under it,
// deals under them. Each node tallies its subtree.
export type GroupBy = "category" | "vendor" | "owner";

interface Tally {
    annualCost: number;
    contractValue: number;
    /** What the subtree has at stake, USD a year: unused plus the overage ahead. */
    atStake: number;
    /** The overage ahead on the consumption deals, USD a year. */
    overage: number;
    deals: number;
    /** Utilization weighted by annual cost: the cost carrying a reading, and that cost times the reading. */
    utilizationWeight: number;
    utilizationSum: number;
    /** The soonest notice date in the subtree. */
    nextNotice?: string;
}

/** The subtree's utilization, cost-weighted over the deals with a reading. */
export const tallyUtilization = (tally: Tally): number | undefined =>
    tally.utilizationWeight > 0 ? tally.utilizationSum / tally.utilizationWeight : undefined;

export type PortfolioNode =
    | ({ kind: "group"; key: string; label: string; children: PortfolioNode[] } & Tally)
    | ({ kind: "vendor"; key: string; vendor: UsageVendor; children: PortfolioNode[] } & Tally)
    | ({ kind: "deal"; key: string; deal: UsageDeal; vendor: UsageVendor } & Tally);

const dealTally = (deal: UsageDeal): Tally => ({
    annualCost: deal.annualCost,
    contractValue: contractValue(deal),
    atStake: atStakeOf(deal),
    overage: overageOf(deal),
    deals: 1,
    utilizationWeight: utilizationOf(deal) != null ? deal.annualCost : 0,
    utilizationSum: deal.annualCost * (utilizationOf(deal) ?? 0),
    nextNotice: daysUntil(deal.term.noticeBy) >= 0 ? deal.term.noticeBy : undefined,
});

const combine = (tallies: Tally[]): Tally => ({
    annualCost: sum(tallies.map((t) => t.annualCost)),
    contractValue: sum(tallies.map((t) => t.contractValue)),
    atStake: sum(tallies.map((t) => t.atStake)),
    overage: sum(tallies.map((t) => t.overage)),
    deals: sum(tallies.map((t) => t.deals)),
    utilizationWeight: sum(tallies.map((t) => t.utilizationWeight)),
    utilizationSum: sum(tallies.map((t) => t.utilizationSum)),
    nextNotice: tallies
        .map((t) => t.nextNotice)
        .filter((d): d is string => !!d)
        .sort()[0],
});

const vendorNode = (vendor: UsageVendor, deals: UsageDeal[], keyPrefix: string): PortfolioNode => {
    // Vendor and deal keys carry their kind: a deal can share its vendor's id.
    const children: PortfolioNode[] = deals.map((deal) => ({
        kind: "deal",
        key: `${keyPrefix}:deal:${deal.id}`,
        deal,
        vendor,
        ...dealTally(deal),
    }));
    return { kind: "vendor", key: `${keyPrefix}:vendor:${vendor.id}`, vendor, children, ...combine(children) };
};

/** The tree rooted at the chosen grouping; by vendor the vendors are the roots. */
export const portfolioTree = (groupBy: GroupBy, deals: UsageDeal[] = DEALS): PortfolioNode[] => {
    const vendorsOf = (subset: UsageDeal[], keyPrefix: string): PortfolioNode[] => {
        const byVendor = new Map<string, UsageDeal[]>();
        for (const deal of subset) {
            byVendor.set(deal.vendorId, [...(byVendor.get(deal.vendorId) ?? []), deal]);
        }
        // The vendors whose agreements the demo drills into lead, in that order; then largest first.
        const rank = (node: PortfolioNode) => {
            const ids = node.kind === "vendor" ? node.children.map((c) => (c.kind === "deal" ? c.deal.id : "")) : [];
            const i = DRILLABLE_DEAL_IDS.findIndex((id) => ids.includes(id));
            return i === -1 ? DRILLABLE_DEAL_IDS.length : i;
        };
        return Array.from(byVendor.entries())
            .map(([vendorId, vendorDeals]) => vendorNode(vendorsById.get(vendorId)!, vendorDeals, keyPrefix))
            .sort((a, b) => rank(a) - rank(b) || b.annualCost - a.annualCost);
    };
    if (groupBy === "vendor") return vendorsOf(deals, "vendor");
    const groups: { key: string; label: string; deals: UsageDeal[] }[] =
        groupBy === "category"
            ? CATEGORIES.map((category) => ({
                  key: category,
                  label: category,
                  deals: deals.filter((d) => vendorOfDeal(d).category === category),
              }))
            : Array.from(new Set(deals.map((d) => d.owner))).sort().map((owner) => ({
                  key: owner,
                  label: owner,
                  deals: deals.filter((d) => d.owner === owner),
              }));
    return groups
        .filter((g) => g.deals.length > 0)
        .map((g) => {
            const children = vendorsOf(g.deals, `${groupBy}:${g.key}`);
            return { kind: "group", key: `${groupBy}:${g.key}`, label: g.label, children, ...combine(children) } as PortfolioNode;
        })
        .sort((a, b) => b.annualCost - a.annualCost);
};

// ---- Microsoft arithmetic ------------------------------------------------

export const microsoftNumbers = (() => {
    const d = MICROSOFT_EA;
    const nextTermCost = d.needed.e5 * E5_UNIT_PRICE + d.needed.e3 * E3_UNIT_PRICE;
    const todayCost = d.paidFor.licenses * E5_UNIT_PRICE;
    return {
        nextTermCost,
        todayCost,
        saving: todayCost - nextTermCost,
        readyToRelease: d.paidFor.neverAssigned + d.used.departed,
        awaitingReview: sum(d.cohorts.map((c) => c.awaitingReview)),
        confirmedByOwners: -d.waterfall.find((s) => s.label.startsWith("No sign-in"))!.value,
        fitE3: sum(d.cohorts.map((c) => c.fitE3)),
        keepE5: sum(d.cohorts.map((c) => c.keepE5)),
        activeUsers: sum(d.cohorts.map((c) => c.activeUsers)),
        plannedHires: d.waterfall.find((s) => s.kind === "plus")!.value,
        daysToNotice: daysUntil(dealsById.get(d.dealId)!.term.noticeBy),
        exempt: 85,
    };
})();

/** The spec's number checks (section 10), asserted once in development. */
export const checkNumbers = () => {
    const d = MICROSOFT_EA;
    const m = microsoftNumbers;
    const walked = sum(d.waterfall.filter((s) => s.kind !== "total").map((s) => s.value)) + d.paidFor.licenses;
    const checks: [string, boolean][] = [
        ["waterfall walks to the needed quantity", walked === d.needed.total],
        ["needed = E5 + E3", d.needed.e5 + d.needed.e3 === d.needed.total],
        ["active = keep E5 + fit E3", m.activeUsers === m.keepE5 + m.fitE3 && m.activeUsers === d.used.active90],
        ["E5 = kept + under review + hires", d.needed.e5 === m.keepE5 + m.awaitingReview + m.plannedHires],
        ["next-term cost rounds to $6.6M", Math.round(m.nextTermCost / 1e5) === 66],
        ["today's cost rounds to $8.5M", Math.round(m.todayCost / 1e5) === 85],
        ["ready to release = 1,110", m.readyToRelease === 1_110],
        ["awaiting review = 1,150", m.awaitingReview === 1_150],
        ["notice is 62 days out", m.daysToNotice === 62],
        ["add-on cost at risk sums to $0.37M", sum(d.skus.slice(1).map((s) => s.costAtRisk)) === 370_000],
    ];
    for (const [name, ok] of checks) {
        // eslint-disable-next-line no-console
        if (!ok) console.error(`Usage demo fixture check failed: ${name}`);
    }
};

// The fixtures are checked once as the module loads, outside production.
if (process.env.NODE_ENV !== "production") checkNumbers();
